import {
  DEFAULT_DATATABLE_PAGINATION,
  DEFAULT_QUERY_LIMIT,
  DEFAULT_QUERY_MAX,
  REGIONS_WITH_CURRENCY,
  ROUTE_PATHS,
  symbolFromCode,
} from "@client-shared/lib/constants";
import i18n from "@client-shared/lib/i18n";
import { isSameObject } from "@client-shared/lib/util";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useVendorContext } from "@client-shared/providers/vendor";
import * as api from "@client-shared/services/api";
import { InvoiceDateRange } from "@client-shared/services/api";
import { handleDisplayErrorData } from "@client-shared/services/api/utilities/handleDisplayErrorData";
import type { InvoiceDateType, InvoiceStats, PurchaseOrderInvoice } from "@shared/types";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useLocation } from "react-router";
import {
  reducer,
  setFetching,
  setInitialized,
  setInvoiceDetails,
  setInvoiceStats,
  setLoading,
  setPagination,
  setParams,
  setSuccess,
} from "./action";
import { PayoutContextState, PayoutState, UsePayoutUtils } from "./types";

const initialState: PayoutState = {
  invoices: [],
  isInitialized: false,
  isLoading: false,
  isSuccess: false,
  isProcessing: false,
  limit: DEFAULT_QUERY_LIMIT,
  max: DEFAULT_QUERY_MAX,
  errors: [],
  pagination: {
    pageSize: DEFAULT_DATATABLE_PAGINATION.pageSize,
    pageIndex: DEFAULT_DATATABLE_PAGINATION.pageIndex,
  },
  invoiceStats: {
    toBePaidAmount: 0,
    unPostedAmount: 0,
    currencySymbol: REGIONS_WITH_CURRENCY.BUS.symbol,
  },
};

export const PayoutContext = React.createContext<PayoutContextState>([initialState, () => {}, {} as UsePayoutUtils]);

const getInvoiceDateRange = (params: PayoutState["params"]): InvoiceDateRange | undefined => {
  if (!params?.startDate && !params?.endDate && !params?.dateType) {
    return undefined;
  }

  return {
    startDate: params.startDate,
    endDate: params.endDate,
    filterDateName: params.dateType,
  };
};

const fetchInvoicesData = async (params: PayoutState["params"]) => {
  const data = await api.getInvoiceDetails(getInvoiceDateRange(params));
  return { invoices: data };
};

export const PayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [authState] = useSFDCAuthState();
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();

  //Get current location to check pathname
  const location = useLocation();
  const isPayoutPath = React.useMemo(() => location.pathname.includes(ROUTE_PATHS.PAYOUTS), [location.pathname]);

  const fetchInvoices = React.useCallback(async () => fetchInvoicesData(state.params), [state.params]);

  //Fetch Invoices
  // Gate on currentVendor.vendorKey, not just state.isInitialized: when a Roaster Admin
  // switches roaster, clearVendorState() removes the vendor while isInitialized stays
  // latched true. Without this guard, queryClient.clear() refetches /payments/invoices
  // with no vendorKey header → authorizer 403 → interceptor logs the admin out.
  const { data, isLoading, error, isSuccess, isFetching } = useQuery({
    queryKey: ["invoices", currentVendor?.vendorKey, state.params?.startDate, state.params?.endDate, state.params?.dateType],
    queryFn: fetchInvoices,
    enabled: state.isInitialized && Boolean(currentVendor?.vendorKey),
  });

  const initialize = React.useCallback(() => {
    if (!currentVendor) {
      return;
    }
    const { pagination, params } = state;
    const newParams = {
      ...params,
      limit: params?.limit ?? DEFAULT_QUERY_LIMIT,
      vendorKey: currentVendor.vendorKey,
    };
    if (!isSameObject(state.pagination, pagination)) {
      dispatch(
        setPagination({
          ...pagination,
          pageIndex: pagination?.pageIndex ?? 0,
          pageSize: pagination?.pageSize ?? 20,
        })
      );
    }
    if (!isSameObject(state.params, newParams)) {
      dispatch(setParams(newParams));
    }
    if (isPayoutPath && isVendorInitialized && authState && !state.isInitialized) {
      dispatch(setInitialized(true));
    }
  }, [authState, currentVendor, isPayoutPath, isVendorInitialized, state]);

  //Set the loading state
  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading]);

  //Set the success state
  useEffect(() => {
    dispatch(setSuccess(isSuccess));
  }, [isSuccess]);

  // Set the fetching state
  useEffect(() => {
    dispatch(setFetching(isFetching));
  }, [isFetching]);

  //set the invoice details
  useEffect(() => {
    if (data?.invoices) {
      const invoices = data.invoices;
      const invoiceStats = calculatePaymentinfo(invoices);
      dispatch(setInvoiceDetails(invoices));
      dispatch(setInvoiceStats(invoiceStats));
    }
  }, [data?.invoices, dispatch]);

  React.useEffect(() => {
    if (error) {
      handleDisplayErrorData(i18n.t("apiErrorMessages.fetchInvoices.message"), i18n.t("apiErrorMessages.fetchInvoices.title"), error);
    }
  }, [error]);

  const fetchInvoicesWithDateRange = React.useCallback(
    (startDate?: string, endDate?: string, dateType?: InvoiceDateType): Promise<void> => {
      dispatch(
        setParams({
          ...state.params,
          startDate,
          endDate,
          dateType,
        })
      );
      return Promise.resolve();
    },
    [state.params]
  );

  const fetchPurchaseOrderLineDetails = React.useCallback(async (purchaseOrderId: string) => {
    try {
      const data = await api.getPurchaseOrderInvoiceLineDetails(purchaseOrderId);
      return data;
    } catch (error) {
      handleDisplayErrorData(
        i18n.t("apiErrorMessages.fetchPOLineDetails.message"),
        i18n.t("apiErrorMessages.fetchPOLineDetails.title"),
        error as Error
      );
      return [];
    }
  }, []);

  const usePayoutUtils: UsePayoutUtils = React.useMemo(() => {
    return {
      initialize,
      fetchInvoicesWithDateRange,
      fetchPurchaseOrderLineDetails,
    };
  }, [initialize, fetchInvoicesWithDateRange, fetchPurchaseOrderLineDetails]);

  const contextValue: PayoutContextState = React.useMemo(() => [state, dispatch, usePayoutUtils], [state, usePayoutUtils]);
  return <PayoutContext.Provider value={contextValue}>{children}</PayoutContext.Provider>;
};

export const calculatePaymentinfo = (invoices: PurchaseOrderInvoice[]): InvoiceStats => {
  let totalUnpaidCents = 0;
  let totalUnpostedLineCents = 0;

  for (const invoice of invoices) {
    if (invoice.invoiceStatus === "Unpaid") {
      const amount = Number(invoice.invoiceAmount);
      totalUnpaidCents += Math.round(amount * 100);
    }
    if (invoice.invoiceStatus === "Unposted") {
      for (const item of invoice.invoiceLines) {
        const amount = Number(item.orderAmount);
        totalUnpostedLineCents += Math.round(amount * 100);
      }
    }
  }
  const invoice = invoices.find((invoice) => invoice.invoiceStatus !== "Unposted");
  const currencySymbol = symbolFromCode(invoice?.currencyCode ?? "USD");
  return {
    toBePaidAmount: totalUnpaidCents / 100,
    unPostedAmount: totalUnpostedLineCents / 100,
    currencySymbol,
  };
};
